// AlphabetChoice.tsx
import { useState, useEffect, useRef } from 'react';
import FireworksLayer from '@/components/interAction/FireworksLayer';
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
import FlipCard from '@/components/interAction/FlipCard'
import * as styles from '@/components/organisms/Learning/LearningSytle.css'
import * as spellStyles from './SpellCheckStyle.css'
import appleImg from '@/assets/img/apple.png'
import * as char from '@/components/interAction/Drag/DragStyle.css'
import { getStudentActivityTitle } from '@/utils/activityTitle';

interface AlphabetChoiceProps {
  activity?: any;
  unitData?: any;
  onResponse?: (response: any) => void;
  existingResponse?: any;
  isHomework?: boolean;
}

function shuffleArray<T>(array: T[]): T[] {
    const arr = [...array]
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1))
      ;[arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }

export default function AlphabetChoice({ activity, unitData, onResponse, existingResponse, isHomework = false }: AlphabetChoiceProps) {
  console.log('=== AlphabetChoice 시작 ===');
  console.log('Activity:', activity);
  console.log('isHomework:', isHomework);
  
  // activity.items에서 실제 데이터 가져오기
  const activityData = activity?.items?.[0] || {};
  let content: any = {};
  
  try {
    const contentJson = activityData.contentJson;
    if (contentJson) {
      content = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
      console.log('Parsed contentJson:', content);
    }
  } catch (e) {
    console.error('contentJson 파싱 에러:', e);
  }

  const word = content.word || content.text || 'apple';
  const imageUrl = content.wordImageUrl || content.imageUrl || content.image;
  const audioUrl = content.audioUrl || content.audio || null;
  
  // 방해 글자(distractors) 자동 생성
  const generateDistractors = (targetWord: string): string[] => {
    const alphabet = 'abcdefghijklmnopqrstuvwxyz';
    const wordLetters = new Set(targetWord.toLowerCase().split(''));
    const availableLetters = alphabet.split('').filter(letter => !wordLetters.has(letter));
    
    // 랜덤하게 3-5개의 방해 글자 선택
    const count = Math.min(Math.floor(Math.random() * 3) + 3, availableLetters.length);
    const result: string[] = [];
    const shuffled = [...availableLetters].sort(() => Math.random() - 0.5);
    
    for (let i = 0; i < count; i++) {
      result.push(shuffled[i]);
    }
    
    return result;
  };
  
  const distractors = content.distractors || generateDistractors(word); // 오답 글자들
  const title = getStudentActivityTitle(activity?.title, '알파벳 고르기');
  
  console.log('=== AlphabetChoice 파싱 결과 ===');
  console.log('Word:', word);
  console.log('ImageUrl:', imageUrl);
  console.log('AudioUrl:', audioUrl);
  console.log('Distractors:', distractors);

  const correctAnswer = word;
  const correctLetters = correctAnswer.split('');
  
  // 알파벳 고르기: 모든 글자를 빈칸으로 (첫 글자도 숨김)
  const visibleIndices: number[] = []; // 아무것도 보이지 않음
  const blankIndices = correctLetters.map((_: string, idx: number) => idx); // 모두 빈칸
  
  console.log('=== AlphabetChoice 데이터 ===');
  console.log('Word:', word);
  console.log('단어 길이:', correctLetters.length);
  console.log('보이는 글자 인덱스:', visibleIndices);
  console.log('빈칸 인덱스:', blankIndices);
  console.log('Distractors:', distractors);
  
  // 글자 ID 생성 (빈칸 글자만, 중복 글자 처리)
  const blankLetterIds = blankIndices.map((idx: number) => {
    const letter = correctLetters[idx];
    const count = correctLetters.slice(0, idx).filter((l: string) => l === letter).length;
    return `${letter}-${count}`;
  });

  // 방해 글자 추가
  const distractorIds = distractors.map((letter: string, index: number) => `${letter}-${index}`);
  const allLetters = shuffleArray([...blankLetterIds, ...distractorIds]);

  // 초기 answer 배열 생성 (알파벳 고르기는 모두 빈칸)
  const getInitialAnswer = () => {
    return new Array(correctLetters.length).fill('');
  };

  // existingResponse에서 이전 답변 복원
  const getInitialState = () => {
    const initialAnswer = getInitialAnswer();
    
    if (existingResponse?.answer) {
      console.log('AlphabetChoice 이전 답변 복원:', existingResponse.answer);
      const previousAnswer = existingResponse.answer;
      const previousAnswerArray = previousAnswer.split(''); // 문자열을 배열로 변환
      
      // 이전 답변을 answer 배열로 복원
      const restoredAnswer = [...initialAnswer];
      const usedLetters: string[] = [];
      
      blankIndices.forEach((blankIdx: number, i: number) => {
        const letter = previousAnswerArray[blankIdx];
        if (letter) {
          const count = previousAnswerArray.slice(0, blankIdx).filter((l: string) => l === letter).length;
          const letterId = `${letter}-${count}`;
          restoredAnswer[blankIdx] = letterId;
          usedLetters.push(letterId);
        }
      });
      
      // 사용된 글자를 제외한 나머지 글자들
      const remainingLetters = allLetters.filter(id => !usedLetters.includes(id));
      
      return {
        answer: restoredAnswer,
        choiceLetters: remainingLetters,
        currentBlankIndex: usedLetters.length
      };
    }
    
    return {
      answer: initialAnswer,
      choiceLetters: allLetters,
      currentBlankIndex: 0
    };
  };
  
  const initialState = getInitialState();
  
  const [choiceLetters, setChoiceLetters] = useState<string[]>(initialState.choiceLetters);
  const [answer, setAnswer] = useState<string[]>(initialState.answer);
  const [showFireworks, setShowFireworks] = useState(false)
  const [flipStatus, setFlipStatus] = useState<'idle' | 'success' | 'fail'>('idle')
  const [currentBlankIndex, setCurrentBlankIndex] = useState(initialState.currentBlankIndex) // 현재 입력 중인 빈칸의 인덱스 (blankIndices 배열 기준)
  const [imageLoadError, setImageLoadError] = useState(false); // 이미지 로드 에러 상태
  const isInitialMount = useRef(true); // 초기 마운트 여부 추적

  // 마운트 시 초기 응답 저장
  useEffect(() => {
    if (onResponse) {
      if (existingResponse?.answer) {
        // 기존 응답이 있으면 그대로 전달
        const userAnswerString = existingResponse.answer;
        const isCorrect = userAnswerString === correctLetters.join('');
        onResponse({ answer: userAnswerString, isCorrect: isCorrect, isHomeworkReady: userAnswerString.trim().length > 0 });
      } else {
        // 새로 시작
        onResponse({ answer: '', isCorrect: false, isHomeworkReady: false });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const isAllFilled =
      answer.length === correctLetters.length && answer.every(Boolean);
    
    // 진행 중 답변도 저장 (이전 버튼으로 돌아올 때 복원용)
    const userAnswerString = answer.map((v) => v ? v.split('-')[0] : '').join('');
    const isCorrect = isAllFilled && userAnswerString === correctLetters.join('');
    
    if (onResponse) {
      onResponse({ 
        answer: userAnswerString,
        isCorrect: isCorrect,
        isHomeworkReady: isAllFilled
      });
    }
  
    if (!isAllFilled) return;
  
    // 초기 마운트 시 이미 완성된 답변으로 복원된 경우에만 애니메이션 스킵
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // existingResponse가 있고 이미 정답을 맞춘 상태로 복원된 경우에만 애니메이션 스킵
      if (existingResponse?.answer && existingResponse.answer === correctLetters.join('')) {
        return;
      }
    }
  
    if (isCorrect) {
      // 학습 모드: 피드백만 표시하고 답변 유지 (이전 버튼으로 돌아올 수 있도록)
      if (!isHomework) {
        setFlipStatus('success');
        setShowFireworks(true);
        
        // 폭죽 효과만 제거하고 답변은 유지
        setTimeout(() => {
          setShowFireworks(false);
          setFlipStatus('idle');
        }, 2000);
      }
    } else {
      // 숙제가 아닐 때만 피드백 표시
      if (!isHomework) {
        setFlipStatus('fail');
      }
  
      // 숙제인 경우 초기화하지 않고 그대로 유지
      if (!isHomework) {
        setTimeout(() => {
          resetGame();
          setFlipStatus('idle');
        }, 1500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer]);

  // 선택지 알파벳 클릭 핸들러 (빈칸에 채우기)
  const handleLetterClick = (letterId: string) => {
    if (currentBlankIndex >= blankIndices.length) return; // 모든 빈칸이 채워진 경우
    
    const targetIndex = blankIndices[currentBlankIndex]; // 실제 단어에서의 인덱스
    const newAnswer = [...answer];
    newAnswer[targetIndex] = letterId;
    setAnswer(newAnswer);
    
    // 사용된 알파벳을 choiceLetters에서 제거
    setChoiceLetters(prev => prev.filter(id => id !== letterId));
    
    // 다음 빈칸으로 이동
    setCurrentBlankIndex(prev => prev + 1);
  };

  // 이미 채워진 글자 클릭 핸들러 (다시 선택지로 돌려보내기)
  const handleAnswerSlotClick = (idx: number) => {
    const letterId = answer[idx];
    if (!letterId) return; // 빈칸이면 무시
    
    // answer에서 해당 위치를 빈칸으로
    const newAnswer = [...answer];
    newAnswer[idx] = '';
    setAnswer(newAnswer);
    
    // choiceLetters에 다시 추가
    setChoiceLetters(prev => [...prev, letterId]);
    
    // currentBlankIndex를 가장 앞의 빈칸으로 설정 (순서대로 채우기 위해)
    let firstEmptyBlankIndex = 0;
    for (let i = 0; i < blankIndices.length; i++) {
      const blankIdx = blankIndices[i];
      if (!newAnswer[blankIdx]) {
        firstEmptyBlankIndex = i;
        break;
      }
    }
    setCurrentBlankIndex(firstEmptyBlankIndex);
  };
  
  const resetGame = () => {
    const resetLetters = shuffleArray(allLetters);
    setChoiceLetters(resetLetters);
    setAnswer(getInitialAnswer());
    setFlipStatus('idle');
    setShowFireworks(false);
    setCurrentBlankIndex(0);
  }

  return (
    <main>
      <h1 className="px-5 pt-5 heading1">{title}</h1>
      <div className="p-5 pt-3">
        <div>
          <FlipCard
            flipStatus={flipStatus}
            front={
              <div className={styles.gameCard}>
                
                <div className="relative size-[150px] border border-gray-100 flex items-center justify-center rounded-2xl overflow-hidden bg-gray-50">
                  {imageUrl && !imageLoadError ? (
                    <img 
                      src={imageUrl} 
                      alt={word} 
                      className="size-[150px] object-cover border border-gray-100 rounded-2xl"
                      onError={(e) => {
                        setImageLoadError(true);
                      }}
                    />
                  ) : (
                    <div className="flex items-center justify-center size-full text-gray-400 font-bold text-sm">
                      이미지가 없습니다.
                    </div>
                  )}
                </div>
                <ListenButton size={48} className="absolute top-4 right-4" audioSrc={audioUrl} />
                <section className={styles.listWrapper}>
                  {correctLetters.map((_: string, idx: number) => {
                    const letterId = answer[idx];
                    const blankOrder = blankIndices.indexOf(idx); // 몇 번째 빈칸인지
                    const isCurrentSlot = blankOrder === currentBlankIndex; // 현재 입력 중인 빈칸인지
                    const isCorrect = !isHomework && letterId && letterId.split('-')[0] === correctLetters[idx];
                    const isWrong = !isHomework && letterId && letterId.split('-')[0] !== correctLetters[idx];
                    
                    return (
                      <div key={idx} className="relative">
                        <div 
                          onClick={() => handleAnswerSlotClick(idx)}
                          className={`${spellStyles.answerSlot} ${
                            !isHomework && isCurrentSlot && !letterId ? spellStyles.currentSlot : ''
                          } ${
                            isCorrect ? spellStyles.correctAnswer : ''
                          } ${
                            isWrong ? spellStyles.wrongAnswer : ''
                          } ${
                            letterId ? 'cursor-pointer hover:opacity-70' : '' // 채워진 글자는 클릭 가능
                          }`}
                        >
                          {letterId ? letterId.split('-')[0] : ''}
                        </div>
                        {!isHomework && isCurrentSlot && !letterId && (
                          <div className="absolute inset-0 rounded-lg border-2 border-yellow-400 border-dashed animate-pulse"></div>
                        )}
                      </div>
                    );
                  })}
                </section>
              </div>
            }
          />
        </div>
        
        {/* 알파벳 선택 영역 */}
        <section className="mt-5">
          <div className="flex flex-wrap gap-2 justify-center items-center p-6 w-full bg-gray-50 rounded-2xl min-h-32">
            {choiceLetters.map((id: string) => {
              const letter = id.split('-')[0];
              
              return (
                <button
                  key={id}
                  onClick={() => handleLetterClick(id)}
                  className={char.charSelect}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </section>
        
        {showFireworks && <FireworksLayer />}
      </div>
    </main>
  )
}

